import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AdminHeader from "@/components/nav/AdminHeader.jsx";
import LayoutShell from "@/components/ui/LayoutShell.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import {
  acceptAdminTeamInvite,
  declineAdminTeamInvite,
  getAdminTeamInviteStatus,
  labelAdminTeamRole,
} from "@/services/adminTeamService.js";

const PANEL_CLASS =
  "rounded-[12px] bg-[rgb(var(--clr-surface-container-high))] shadow-[var(--elev-1)]";

function cleanText(value) {
  return String(value || "").trim();
}

export default function AdminTeamAccept() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const auth = useAuth() || {};
  const token = cleanText(params.get("token"));
  const invitedEmail = cleanText(params.get("email")).toLowerCase();
  const proofOnly = cleanText(params.get("proof")) === "1";
  const signedInEmail = cleanText(auth?.user?.email).toLowerCase();
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [loadingInvite, setLoadingInvite] = React.useState(false);
  const [invite, setInvite] = React.useState(null);
  const [accepted, setAccepted] = React.useState(null);
  const [declined, setDeclined] = React.useState(false);

  const emailMismatch = invitedEmail && signedInEmail && invitedEmail !== signedInEmail;
  const missingToken = !token;
  const inviteStatus = cleanText((accepted || invite)?.status).toLowerCase();
  const isInvited = inviteStatus === "invited";
  const actionsDisabled = !!busy || loadingInvite || !isInvited || proofOnly || missingToken || emailMismatch;

  async function continueToAdmin() {
    try {
      await auth?.refreshProfile?.();
    } catch {
      // The admin route guard still validates access. Refresh is best-effort to
      // pick up freshly accepted admin-team roles before navigation.
    }
    nav("/admin/users");
  }

  React.useEffect(() => {
    let alive = true;
    if (!token || proofOnly || emailMismatch) {
      setInvite(null);
      setLoadingInvite(false);
      return () => {
        alive = false;
      };
    }

    setLoadingInvite(true);
    setError("");
    getAdminTeamInviteStatus(token)
      .then((nextInvite) => {
        if (!alive) return;
        setInvite(nextInvite || null);
      })
      .catch((statusError) => {
        if (!alive) return;
        setError(statusError?.message || "Could not load this admin team invitation.");
      })
      .finally(() => {
        if (!alive) return;
        setLoadingInvite(false);
      });

    return () => {
      alive = false;
    };
  }, [emailMismatch, proofOnly, token]);

  async function accept() {
    if (actionsDisabled) return;
    setBusy("accept");
    setError("");
    try {
      const result = await acceptAdminTeamInvite(token);
      setAccepted(result);
      setInvite(result);
    } catch (acceptError) {
      setError(acceptError?.message || "Could not accept this admin team invitation.");
    }
    setBusy("");
  }

  async function decline() {
    if (actionsDisabled) return;
    setBusy("decline");
    setError("");
    try {
      const result = await declineAdminTeamInvite(token);
      setInvite(result);
      setDeclined(true);
    } catch (declineError) {
      setError(declineError?.message || "Could not decline this admin team invitation.");
    }
    setBusy("");
  }

  const roleLabel = labelAdminTeamRole((accepted || invite)?.role || "support");
  const alreadyAccepted = inviteStatus === "active";
  const isDeactivated = inviteStatus === "deactivated";
  const isDeclined = inviteStatus === "declined" || declined;

  return (
    <LayoutShell hideTabs flushTop freeScroll manageOwnTabLayout railClassName="w-full">
      <AdminHeader section="team" />
      <SettingsScreenShell>
        <TopBarCenter
          title="Admin team invitation"
          subtitle="Review and accept internal AfriMed™ admin access."
          onBack={() => nav("/admin/users")}
        />

        <div className={`${PANEL_CLASS} p-4 md:p-5`}>
          {accepted ? (
            <>
              <div>
                <h2 className="text-title-large text-on-surface">Invitation accepted</h2>
              </div>
              <div className="mt-3">
                <p className="text-body-medium text-on-surface-var">
                  Your AfriMed™ admin team access is now active as {roleLabel}.
                </p>
              </div>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={continueToAdmin}
                  className="inline-flex h-10 items-center justify-center rounded-[10px] bg-[rgb(var(--afrimed-primary))] px-4 text-label-large text-[rgb(var(--afrimed-on-primary))]"
                >
                  Continue to admin
                </button>
              </div>
            </>
          ) : alreadyAccepted ? (
            <>
              <div>
                <h2 className="text-title-large text-on-surface">Invitation already accepted</h2>
              </div>
              <div className="mt-3">
                <p className="text-body-medium text-on-surface-var">
                  Your AfriMed™ admin team access is already active as {roleLabel}.
                </p>
              </div>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={continueToAdmin}
                  className="inline-flex h-10 items-center justify-center rounded-[10px] bg-[rgb(var(--afrimed-primary))] px-4 text-label-large text-[rgb(var(--afrimed-on-primary))]"
                >
                  Continue to admin
                </button>
              </div>
            </>
          ) : isDeactivated ? (
            <>
              <div>
                <h2 className="text-title-large text-on-surface">Admin access deactivated</h2>
              </div>
              <div className="mt-3">
                <p className="text-body-medium text-on-surface-var">
                  This AfriMed™ admin team access is deactivated. Please contact AfriMed Admin if this looks incorrect.
                </p>
              </div>
            </>
          ) : isDeclined ? (
            <>
              <div>
                <h2 className="text-title-large text-on-surface">Invitation declined</h2>
              </div>
              <div className="mt-3">
                <p className="text-body-medium text-on-surface-var">
                  This AfriMed™ admin team invitation was declined. No admin access has been activated.
                </p>
              </div>
            </>
          ) : (
            <>
              <div>
                <h2 className="text-title-large text-on-surface">Review invitation</h2>
              </div>
              <div className="mt-3">
                <p className="text-body-medium text-on-surface-var">
                  You have been invited to join the AfriMed™ admin team. Admin access only becomes active if you accept this invitation while signed in with the invited email.
                </p>
                {invitedEmail ? (
                  <p className="mt-3 text-body-small text-on-surface-var">Invited email: {invitedEmail}</p>
                ) : null}
                {signedInEmail ? (
                  <p className="mt-1 text-body-small text-on-surface-var">Signed in as: {signedInEmail}</p>
                ) : null}

                {proofOnly ? (
                  <div className="mt-4 rounded-[10px] bg-[rgba(var(--afrimed-tertiary),0.12)] px-3 py-2 text-body-medium text-[rgb(var(--afrimed-tertiary))]">
                    Proof-only invitation preview. Acceptance is disabled and no admin access can be activated from this link.
                  </div>
                ) : null}

                {missingToken && !proofOnly ? (
                  <div className="mt-4 rounded-[10px] bg-[var(--state-critical-08)] px-3 py-2 text-body-medium text-[rgb(var(--schemes-critical))]">
                    This invitation link is missing its secure token. Please ask a super admin for a fresh invitation.
                  </div>
                ) : null}

                {emailMismatch ? (
                  <div className="mt-4 rounded-[10px] bg-[var(--state-critical-08)] px-3 py-2 text-body-medium text-[rgb(var(--schemes-critical))]">
                    This invitation was issued to a different email. Sign in with {invitedEmail} to continue.
                  </div>
                ) : null}

                {error ? (
                  <div className="mt-4 rounded-[10px] bg-[var(--state-critical-08)] px-3 py-2 text-body-medium text-[rgb(var(--schemes-critical))]">
                    {error}
                  </div>
                ) : null}

                {loadingInvite ? (
                  <p className="mt-4 text-body-small text-on-surface-var">Checking invitation status...</p>
                ) : null}
              </div>
              {isInvited ? (
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={accept}
                    disabled={actionsDisabled}
                    className="inline-flex h-10 items-center justify-center rounded-[10px] bg-[rgb(var(--afrimed-primary))] px-4 text-label-large text-[rgb(var(--afrimed-on-primary))] disabled:opacity-50"
                  >
                    {busy === "accept" ? "Accepting..." : "Accept invitation"}
                  </button>
                  <button
                    type="button"
                    onClick={decline}
                    disabled={actionsDisabled}
                    className="inline-flex h-10 items-center justify-center rounded-[10px] border border-[rgba(var(--schemes-critical),0.45)] bg-[var(--state-critical-08)] px-4 text-label-large text-[rgb(var(--schemes-critical))] disabled:opacity-50"
                  >
                    {busy === "decline" ? "Declining..." : "Decline"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </SettingsScreenShell>
    </LayoutShell>
  );
}
